import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { analyzeAudit, getAudit } from "@/lib/api";
import { useAwakeMember } from "@/lib/awakeContext";
import { toast } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";

const STEPS = [
  "Parsing profile signal",
  "Extracting positioning keywords",
  "Reading tone of voice",
  "Grading headline & About",
  "Scoring engagement & authenticity",
  "Drafting your strategic overview",
  "Generating blog titles & sample posts",
  "Compiling your audit",
];

// Estimated total duration in seconds — used for progress bar pacing.
const EST_SECONDS = 90;
// Poll every N ms for audit status.
const POLL_INTERVAL_MS = 3000;
// Hard stop after N ms. Dense profiles on slow upstream days can legitimately
// take 120-150s end-to-end; 5 minutes gives plenty of headroom while still
// catching the truly stuck case.
const POLL_MAX_MS = 300000;

function parseStatusError(detail) {
  if (typeof detail !== "string") return "We couldn't complete your audit. Please try again in a moment.";
  if (/LLM_BUDGET_EXCEEDED|budget.*(exceed|exhaust)/i.test(detail)) {
    return "Audit credits on this account are exhausted. Top up the Emergent Universal Key (Profile → Universal Key → Add Balance) and try again — your inputs are saved.";
  }
  if (/502|bad gateway|provider/i.test(detail)) {
    return "Our AI partner is having a moment. Wait 30 seconds and try again — your inputs are safe.";
  }
  return detail;
}

function parsePostError(e) {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  if (status === 422) {
    const first = Array.isArray(detail) ? detail[0] : null;
    if (first?.loc?.includes("email")) return "Please add a valid email and try again.";
    if (first?.msg) return first.msg.charAt(0).toUpperCase() + first.msg.slice(1) + ".";
    return "Some of your details look off — please review and try again.";
  }
  if (status === 400)
    return typeof detail === "string" ? detail : "Profile text is too short. Add more detail and retry.";
  if (typeof detail === "string") return detail;
  return "We couldn't queue your audit. Please try again.";
}

export default function Run() {
  const navigate = useNavigate();
  const { isMember, dashboardUrl } = useAwakeMember();
  const [stepIdx, setStepIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [errorKind, setErrorKind] = useState(null); // "budget" | "transient" | null
  const [retrying, setRetrying] = useState(false);
  const calledRef = useRef(false);
  const stepRef = useRef(null);
  const elapsedRef = useRef(null);
  const startedAtRef = useRef(null);
  const pollRef = useRef(null);
  const pollStartedAtRef = useRef(null);
  const auditIdRef = useRef(null);

  const stopTickers = () => {
    if (stepRef.current) {
      clearInterval(stepRef.current);
      stepRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const startTickers = useCallback(() => {
    stopTickers();
    setStepIdx(0);
    setElapsed(0);
    startedAtRef.current = Date.now();
    const stepInterval = Math.round((EST_SECONDS * 1000) / STEPS.length);
    stepRef.current = setInterval(() => {
      setStepIdx((i) => (i < STEPS.length - 1 ? i + 1 : i));
    }, stepInterval);
    elapsedRef.current = setInterval(() => {
      const secs = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(secs);
    }, 250);
  }, []);

  const showError = useCallback((msg, kind) => {
    stopTickers();
    setErrorMsg(msg);
    setErrorKind(kind);
    toast.error(msg);
  }, []);

  const pollAudit = useCallback(async () => {
    const audit_id = auditIdRef.current;
    if (!audit_id) return;
    // Stop polling on timeout
    if (Date.now() - pollStartedAtRef.current > POLL_MAX_MS) {
      showError(
        "Your audit is taking longer than expected. Your inputs are saved — please try again.",
        "transient"
      );
      return;
    }
    try {
      const audit = await getAudit(audit_id);
      if (audit?.status === "complete") {
        sessionStorage.removeItem("audit_pending");
        sessionStorage.setItem(`audit_${audit_id}`, JSON.stringify(audit));
        stopTickers();
        navigate(`/results/${audit_id}`, { replace: true });
        return;
      }
      if (audit?.status === "error") {
        const detail = audit.error_detail || "";
        const kind = audit.error_code === "budget" ? "budget" : "transient";
        showError(parseStatusError(detail), kind);
        return;
      }
      // still running — schedule the next poll
      pollRef.current = setTimeout(pollAudit, POLL_INTERVAL_MS);
    } catch {
      // transient network/ingress error — keep polling, the doc is in Mongo
      pollRef.current = setTimeout(pollAudit, POLL_INTERVAL_MS);
    }
  }, [navigate, showError]);

  const runAudit = useCallback(async () => {
    const raw = sessionStorage.getItem("audit_pending");
    if (!raw) {
      navigate("/");
      return;
    }
    setErrorMsg(null);
    setErrorKind(null);
    startTickers();
    try {
      const payload = JSON.parse(raw);
      const queued = await analyzeAudit(payload);
      if (!queued?.audit_id) {
        showError("We couldn't queue your audit. Please try again.", "transient");
        return;
      }
      auditIdRef.current = queued.audit_id;
      pollStartedAtRef.current = Date.now();
      // Start polling for completion
      pollRef.current = setTimeout(pollAudit, POLL_INTERVAL_MS);
    } catch (e) {
      const msg = parsePostError(e);
      showError(msg, "transient");
    } finally {
      setRetrying(false);
    }
  }, [navigate, pollAudit, showError, startTickers]);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    runAudit();
    // Intentionally no cleanup: in React Strict Mode the effect double-mounts,
    // and the cleanup would prematurely kill the tickers / polling state.
    // The success and error paths explicitly call stopTickers() before navigating,
    // and intervals are naturally torn down when the user leaves this page.
  }, [runAudit]);

  // When user switches back to this tab, browsers may have throttled our
  // setTimeout polling (Chrome throttles background tabs to ~once/minute).
  // Force an immediate poll so we don't appear to be doing nothing.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && auditIdRef.current && !errorMsg) {
        if (pollRef.current) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
        }
        pollAudit();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [pollAudit, errorMsg]);

  const handleRetry = () => {
    setRetrying(true);
    calledRef.current = true; // already true, just for clarity
    runAudit();
  };

  if (errorMsg) {
    const isBudget = errorKind === "budget";
    return (
      <div className="section-pad py-20 md:py-24 min-h-[70vh] flex flex-col">
        <div className="card-tech rounded-sm p-6 md:p-8 max-w-2xl">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-[var(--red)] mt-1 shrink-0" />
            <div className="flex-1">
              <div className="overline mb-1" style={{ color: "var(--red)" }}>
                {isBudget ? "Audit credits exhausted" : "Something went wrong"}
              </div>
              <h2 className="font-display text-2xl tracking-tight">
                {isBudget
                  ? "We need to top up before we can run your audit."
                  : "We couldn't complete your audit."}
              </h2>
              <p className="text-[var(--ink-2)] mt-3">{errorMsg}</p>
              {isBudget && (
                <div
                  className="mt-5 rounded-sm border-l-4 p-4 bg-[var(--bg-2)] text-sm text-[var(--ink-2)] leading-relaxed"
                  style={{ borderColor: "var(--yellow-deep)" }}
                  data-testid="budget-help-callout"
                >
                  <span className="font-medium text-[var(--ink)]">What this means:</span>{" "}
                  Each audit costs Emergent Universal Key credit (a few cents of Claude Sonnet 4.5).
                  The balance on this key has run out. Once topped up, this page will work again
                  immediately — your inputs are saved against your email.
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-6">
                {!isBudget && (
                  <button
                    onClick={handleRetry}
                    disabled={retrying}
                    className="btn-red rounded-full h-11 px-5 inline-flex items-center gap-2"
                    data-testid="run-retry-btn"
                  >
                    {retrying ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Retrying…
                      </>
                    ) : (
                      "Try again"
                    )}
                  </button>
                )}
                <button
                  onClick={() => navigate("/#audit-form")}
                  className="rounded-full h-11 px-5 inline-flex items-center border-2 border-[var(--line)]"
                  data-testid="run-edit-btn"
                >
                  {isBudget ? "Back to my details" : "Edit my details"}
                </button>
                {isMember ? (
                  <a
                    href={dashboardUrl}
                    className="rounded-full h-11 px-5 inline-flex items-center text-[var(--ink-2)] hover:text-[var(--red)] underline-offset-4 hover:underline"
                  >
                    ← Back to your dashboard
                  </a>
                ) : (
                  <a
                    href="https://awake.wearetheweave.co.uk/join"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full h-11 px-5 inline-flex items-center text-[var(--ink-2)] hover:text-[var(--red)] underline-offset-4 hover:underline"
                  >
                    Skip and join The Weave →
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Progress bar — clamp at 92% so it never looks "done" while still waiting.
  const progressPct = Math.min(92, (elapsed / EST_SECONDS) * 100);
  const secondsLeft = Math.max(0, Math.ceil(EST_SECONDS - elapsed));
  const overtime = elapsed > EST_SECONDS;

  return (
    <div data-testid="run-page" className="section-pad py-16 md:py-20 min-h-[80vh] flex flex-col">
      {/* sticky-ish progress strip */}
      <div className="flex items-center gap-3 mb-5">
        <Loader2 className="animate-spin text-[var(--teal)]" size={20} />
        <div className="overline">
          {overtime
            ? "Still working — Claude is producing your audit. Rich profiles can take a little longer."
            : `Audit in progress · ~${secondsLeft}s remaining`}
        </div>
      </div>

      <div className="bar-track mb-8" style={{ height: 8 }}>
        <div
          className="bar-fill"
          style={{
            width: `${progressPct}%`,
            background:
              "linear-gradient(90deg, var(--teal), var(--yellow), var(--red))",
            height: 8,
          }}
        />
      </div>

      {/* Expectation-setting strip — clarifies how report arrives */}
      <div
        data-testid="run-delivery-note"
        className="mb-10 rounded-sm border border-[var(--line)] bg-[var(--bg-2)] px-4 py-3 text-xs md:text-sm text-[var(--ink-2)] leading-relaxed"
      >
        <span className="font-medium text-[var(--ink)]">Stay on this page.</span>{" "}
        The moment your audit is ready, it'll open here in full — no inbox refresh needed.
        You'll be able to <span className="font-mono-mark text-[11px]">Download / Print</span>{" "}
        to PDF or bookmark the URL to come back.
      </div>

      <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight max-w-3xl text-center md:text-left mx-auto md:mx-0">
        We're <span className="text-[var(--teal)]">reading carefully</span> —
        <br className="hidden md:block" />
        the way a thoughtful mentor would.
      </h1>

      <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-start">
        <ol className="space-y-3 font-mono-mark text-sm">
          {STEPS.map((s, i) => {
            const state = i < stepIdx ? "done" : i === stepIdx ? "active" : "pending";
            return (
              <li
                key={s}
                data-testid={`step-${i}`}
                className={`flex items-center gap-3 transition-colors duration-300 ${
                  state === "pending" ? "text-[var(--muted)]" : ""
                } ${state === "active" ? "text-[var(--teal)]" : ""}`}
              >
                <span className="w-5 inline-flex items-center justify-center">
                  {state === "done" ? (
                    <CheckCircle2 size={14} className="text-[var(--teal)]" />
                  ) : state === "active" ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-40" />
                  )}
                </span>
                <span className="uppercase tracking-[0.16em]">{s}</span>
                {state === "active" && (
                  <span className="ml-auto text-[10px] blink">running…</span>
                )}
              </li>
            );
          })}
        </ol>

        <div className="card-tech rounded-sm p-6 md:p-8">
          <div className="overline">What you're about to receive</div>
          <ul className="mt-4 space-y-3 text-[var(--ink-2)] text-sm md:text-base">
            <li>· Overall score (0–100) + 11-category scorecard</li>
            <li>· Strategic overview of what you currently communicate</li>
            <li>· 3–4 content pillars to anchor your posting</li>
            <li>· 10 blog title ideas with angles</li>
            <li>· 3 sample LinkedIn posts in your tone</li>
            <li>· A tone-of-voice profile (do / don't)</li>
          </ul>
          <div className="hairline my-6" />
          <div className="overline mb-2" style={{ color: "var(--teal)" }}>
            Next step · members
          </div>
          <p className="text-sm text-[var(--ink-2)]">
            Inside The Weave, your audit becomes the spine of a personalised Founder Branding
            Coach — mission builder, content pillars, weekly drafts, AI feedback. Members only.
          </p>
        </div>
      </div>
    </div>
  );
}
